import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type CopilotAction = 'suggestion' | 'playbook' | 'health' | 'sentiment' | 'ask';

export type CopilotSide = 'attendant' | 'copilot';
export type CopilotStatus = 'pending' | 'success' | 'error';

/**
 * Surface-level actions the UI is allowed to render. The persistence model
 * supports the full 8-agent enum, but auto-fired entries (evaluation,
 * standards, insight) are filtered out of the chat thread in v1.
 */
export const DISPLAYABLE_ACTIONS: ReadonlySet<string> = new Set([
  'suggestion',
  'playbook',
  'health',
  'sentiment',
  'ask',
]);

export interface CopilotChatMessage {
  _id?: string;
  requestId: string;
  side: CopilotSide;
  action: string;
  instruction?: string;
  status?: CopilotStatus;
  result?: Record<string, unknown>;
  errorMessage?: string;
  createdAt: string | Date;
}

export interface CopilotState {
  idAttendance: string | null;
  messages: CopilotChatMessage[];   // append-only; ordered by createdAt asc
  instructionDraft: string;
  loading: boolean;                 // initial history fetch only
  pendingRequestIds: string[];      // requestIds currently awaiting copilot response
  scrollAnchored: boolean;          // true when user is at (or near) the bottom
  socketConnected: boolean;
  error: string | null;
}

const INITIAL_STATE: CopilotState = {
  idAttendance: null,
  messages: [],
  instructionDraft: '',
  loading: false,
  pendingRequestIds: [],
  scrollAnchored: true,
  socketConnected: false,
  error: null,
};

@Injectable({ providedIn: 'root' })
export class AttendanceCopilotStateService {
  private readonly state$ = new BehaviorSubject<CopilotState>({ ...INITIAL_STATE });

  get snapshot(): CopilotState {
    return this.state$.getValue();
  }

  select(): Observable<CopilotState> {
    return this.state$.asObservable();
  }

  // ─── lifecycle ─────────────────────────────────────────────────────────────

  resetForAttendance(idAttendance: string): void {
    this.state$.next({
      ...INITIAL_STATE,
      idAttendance,
      socketConnected: this.snapshot.socketConnected,
    });
  }

  reset(): void {
    this.state$.next({ ...INITIAL_STATE });
  }

  // ─── input draft ───────────────────────────────────────────────────────────

  setInstruction(instructionDraft: string): void {
    this.patch({ instructionDraft });
  }

  clearInstruction(): void {
    this.patch({ instructionDraft: '' });
  }

  // ─── socket / scroll state ─────────────────────────────────────────────────

  setSocketConnected(socketConnected: boolean): void {
    this.patch({ socketConnected });
  }

  setScrollAnchored(scrollAnchored: boolean): void {
    this.patch({ scrollAnchored });
  }

  setLoading(loading: boolean): void {
    this.patch({ loading });
  }

  // ─── history hydration ─────────────────────────────────────────────────────

  loadHistory(messages: CopilotChatMessage[]): void {
    const sorted = [...messages].sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const pendingRequestIds = sorted
      .filter((m) => m.side === 'copilot' && m.status === 'pending')
      .map((m) => m.requestId);
    this.patch({ messages: sorted, pendingRequestIds, loading: false, error: null });
  }

  prependOlder(older: CopilotChatMessage[]): void {
    if (older.length === 0) return;
    const sorted = [...older].sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    this.patch({ messages: [...sorted, ...this.snapshot.messages] });
  }

  // ─── append (manual trigger) ───────────────────────────────────────────────

  appendAttendantMessage(msg: CopilotChatMessage): void {
    this.patch({ messages: [...this.snapshot.messages, msg] });
  }

  appendPendingCopilotMessage(msg: CopilotChatMessage): void {
    this.patch({
      messages: [...this.snapshot.messages, msg],
      pendingRequestIds: [...this.snapshot.pendingRequestIds, msg.requestId],
    });
  }

  /**
   * Used when the API responds with a server-side requestId different from the
   * optimistic one the UI generated. Re-key both the attendant and copilot
   * rows that were appended together.
   */
  replaceRequestId(oldRequestId: string, newRequestId: string): void {
    const messages = this.snapshot.messages.map((m) =>
      m.requestId === oldRequestId ? { ...m, requestId: newRequestId } : m,
    );
    const pendingRequestIds = this.snapshot.pendingRequestIds.map((id) =>
      id === oldRequestId ? newRequestId : id,
    );
    this.patch({ messages, pendingRequestIds });
  }

  // ─── resolve / reject (socket-driven) ──────────────────────────────────────

  resolveCopilotMessage(requestId: string, result: Record<string, unknown>): void {
    const messages = this.snapshot.messages.map((m) =>
      m.requestId === requestId && m.side === 'copilot'
        ? { ...m, status: 'success' as const, result }
        : m,
    );
    const pendingRequestIds = this.snapshot.pendingRequestIds.filter((id) => id !== requestId);
    this.patch({ messages, pendingRequestIds });
  }

  rejectCopilotMessage(requestId: string, errorMessage: string): void {
    const messages = this.snapshot.messages.map((m) =>
      m.requestId === requestId && m.side === 'copilot'
        ? { ...m, status: 'error' as const, errorMessage }
        : m,
    );
    const pendingRequestIds = this.snapshot.pendingRequestIds.filter((id) => id !== requestId);
    this.patch({ messages, pendingRequestIds, error: errorMessage });
  }

  // ─── utilities ─────────────────────────────────────────────────────────────

  private patch(partial: Partial<CopilotState>): void {
    this.state$.next({ ...this.snapshot, ...partial });
  }
}
